import { useState } from "react";

import type { DesktopBridge, LegacyImportSummary } from "./bridge.js";

/**
 * The settings screen's "import from the old MongoDB database" action
 * (desktop, host role only). The operator picks the exported dump files, the
 * main process reads them and writes into the live database
 * (`apps/desktop/src/legacyImport.ts`), and this shows a summary of what
 * landed.
 *
 * The import is idempotent — re-importing the same file converges rather than
 * duplicating — but it still modifies the live database, so an explicit inline
 * confirm sits between the button and the write. The picker runs in the main
 * process (a sandboxed renderer cannot read file paths), so all this component
 * ever holds are the absolute paths the bridge hands back.
 */
export function LegacyImport({ bridge }: { bridge: DesktopBridge }) {
  const [sources, setSources] = useState<readonly string[]>([]);
  const [employeesPath, setEmployeesPath] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ step: "idle" });
  const [error, setError] = useState<string | null>(null);

  const running = phase.step === "running";

  async function pick(kind: "sources" | "employees") {
    setError(null);
    try {
      const paths = await bridge.legacyImport.pickFiles(kind);
      if (paths.length === 0) return; // Cancelled — keep the current selection.
      if (kind === "sources") setSources(paths);
      else setEmployeesPath(paths[0] ?? null);
      setPhase({ step: "idle" }); // A new selection supersedes any previous result.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function runImport() {
    setError(null);
    setPhase({ step: "running" });
    try {
      const result = await bridge.legacyImport.run({ sources, employeesPath });
      if (result.ok) {
        setPhase({ step: "done", summary: result.summary });
      } else {
        setError(result.message);
        setPhase({ step: "idle" });
      }
    } catch (err) {
      // Only a truly unexpected transport failure lands here; expected problems
      // (a bad file, a client install) come back as `{ ok: false }` above.
      setError(err instanceof Error ? err.message : String(err));
      setPhase({ step: "idle" });
    }
  }

  return (
    <div className="legacy-import">
      <p className="legacy-import-help">
        Uvozi zapise iz izvožene stare baze (MongoDB). Datoteke ostanejo nespremenjene; zapisi se dodajo ali
        posodobijo v trenutni bazi — ponovni uvoz iste datoteke ne podvaja.
      </p>

      <div className="legacy-import-pick">
        <button type="button" onClick={() => void pick("sources")} disabled={running}>
          Izberi datoteke z zapisi …
        </button>
        <span className={`legacy-import-files${sources.length === 0 ? " legacy-import-files-empty" : ""}`}>
          {sources.length === 0 ? "Nobena datoteka še ni izbrana" : sources.map(basename).join(", ")}
        </span>
      </div>

      <div className="legacy-import-pick">
        <button type="button" onClick={() => void pick("employees")} disabled={running}>
          Izberi datoteko z zaposlenimi …
        </button>
        <span className={`legacy-import-files${employeesPath === null ? " legacy-import-files-empty" : ""}`}>
          {employeesPath === null ? "Neobvezno" : basename(employeesPath)}
        </span>
        {employeesPath !== null && (
          <button type="button" className="legacy-import-clear" onClick={() => setEmployeesPath(null)} disabled={running}>
            Odstrani
          </button>
        )}
      </div>

      {error && (
        <p className="settings-error" role="alert">
          {error}
        </p>
      )}

      {phase.step === "confirm" ? (
        <div className="legacy-import-confirm">
          <p>
            Uvozim {sources.length === 1 ? "izbrano datoteko" : `izbrane datoteke (${sources.length})`} v trenutno
            bazo?
          </p>
          <div className="legacy-import-confirm-actions">
            <button type="button" onClick={() => setPhase({ step: "idle" })}>
              Prekliči
            </button>
            <button type="button" className="legacy-import-run" onClick={() => void runImport()}>
              Da, uvozi
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="legacy-import-run"
          onClick={() => setPhase({ step: "confirm" })}
          disabled={running || sources.length === 0}
        >
          {running ? "Uvažanje …" : "Uvozi"}
        </button>
      )}

      {phase.step === "done" && <ImportSummaryView summary={phase.summary} />}
    </div>
  );
}

type Phase =
  | { step: "idle" }
  | { step: "confirm" }
  | { step: "running" }
  | { step: "done"; summary: LegacyImportSummary };

/** The last path segment of an absolute path — what the operator recognises, not the full disk location. */
function basename(path: string): string {
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1] || path;
}

/**
 * Turns the importer's internal season label (`zimske-2025`, `OPEN`) into the
 * wording the shop uses ("Zimske 2025", "Odprta sezona"). Any unrecognised
 * label is shown as-is rather than hidden.
 */
function formatSeasonLabel(label: string): string {
  if (label === "OPEN") return "Odprta sezona";
  const match = /^(letne|zimske)-(\d{4})$/.exec(label);
  if (match) {
    return `${match[1] === "letne" ? "Letne" : "Zimske"} ${match[2]}`;
  }
  return label;
}

function ImportSummaryView({ summary }: { summary: LegacyImportSummary }) {
  const warnings: string[] = [];
  if (summary.droppedEmpty > 0) warnings.push(`${summary.droppedEmpty} praznih zapisov izpuščenih`);
  if (summary.quarantined > 0) warnings.push(`${summary.quarantined} zapisov v karanteni (neuporabnih)`);
  if (summary.numberCollisions > 0) warnings.push(`${summary.numberCollisions} sporov pri številkah mest`);
  if (summary.fieldDisagreements > 0) warnings.push(`${summary.fieldDisagreements} neskladij med datotekami`);

  return (
    <div className="legacy-import-summary" role="status">
      <p className="legacy-import-summary-headline">
        Uvoženih {summary.imported} od {summary.totalMergedRecords} zapisov
        {summary.employees > 0 ? `, ${summary.employees} zaposlenih` : ""}.
      </p>
      {summary.seasons.length > 0 && (
        <ul className="legacy-import-seasons">
          {summary.seasons.map((s) => (
            <li key={s.label}>
              <span>{formatSeasonLabel(s.label)}</span>
              <span>{s.count}</span>
            </li>
          ))}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul className="legacy-import-warnings">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
